import { makeLocalId } from "~/shared/lib/local-id";
import type { PendingUpload } from "~/shared/store/types";

import type { PerKindCounts, PerKindLimits, PickerSourceItem, SelectionDisabledReason } from "./types";

const ALL_KINDS = ["image", "video", "audio"] as const;

/** Per-kind количество выбранных ключей (по kind item'а из `items`). */
export function countSelectedByKind(
    items: PickerSourceItem[],
    selectedKeys: Iterable<string>,
): PerKindCounts {
    const counts: PerKindCounts = { image: 0, video: 0, audio: 0 };
    const byKey = new Map(items.map((it) => [it.s3Key, it] as const));
    for (const key of selectedKeys) {
        const it = byKey.get(key);
        if (it) counts[it.kind] += 1;
    }
    return counts;
}

/** Per-kind свободные слоты после уже-в-pending + уже-выбранного-в-picker'е. */
export function computeRemainingByKind(
    limits: PerKindLimits,
    currentCounts: PerKindCounts,
    selectedByKind: PerKindCounts,
): PerKindCounts {
    const r: PerKindCounts = { image: 0, video: 0, audio: 0 };
    for (const k of ALL_KINDS) {
        r[k] = Math.max(0, (limits[k] ?? 0) - currentCounts[k] - selectedByKind[k]);
    }
    return r;
}

/** Причина, по которой тайл нельзя выбрать (или null — можно). */
export function resolveSelectionReason(params: {
    alreadyAdded: boolean;
    acceptsKind: boolean;
    isSelected: boolean;
    remainingForKind: number;
}): SelectionDisabledReason | null {
    const { alreadyAdded, acceptsKind, isSelected, remainingForKind } = params;
    if (alreadyAdded) return "alreadyAdded";
    if (!acceptsKind) return "kindNotAccepted";
    if (!isSelected && remainingForKind === 0) return "limitReached";
    return null;
}

/** Превратить выбранные ключи в `PendingUpload[]` (готовые, uploaded/generated). */
export function selectionToPendingUploads(
    items: PickerSourceItem[],
    selectedKeys: Iterable<string>,
): PendingUpload[] {
    const byKey = new Map(items.map((it) => [it.s3Key, it] as const));
    const result: PendingUpload[] = [];
    for (const s3Key of selectedKeys) {
        const it = byKey.get(s3Key);
        if (!it) continue;
        result.push({
            localId: makeLocalId("picked", it.s3Key),
            fileName: it.fileName,
            kind: it.kind,
            mimeType: it.mimeType,
            sizeBytes: it.sizeBytes ?? 0,
            status: "done",
            progress: 100,
            s3Key: it.s3Key,
            previewUrl: it.s3Url,
        });
    }
    return result;
}
